import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth.store';
import { getPermissionsMatrix, type Permission } from '../api/permissions.api';

export type { Permission };

/**
 * F0.3 — Los permisos vienen del backend (`GET /api/permissions/matrix`), que
 * los deriva de `rbac.model.ts`.
 *
 * Antes este fichero tenía su propia tabla `ROLE_PERMISSIONS` con un
 * vocabulario distinto al del backend (`view:finanzas` vs `finance:read`), y
 * `Sidebar` mantenía una tercera por rutas. Las tres se desincronizaban: el
 * menú ofrecía secciones que el API rechazaba con 403, y al revés.
 *
 * Mientras la matriz viaja —o si la petición falla— solo valen los
 * `permissionOverrides` que ya trae el JWT. Fallar cerrado evita el parpadeo de
 * botones de administración que el backend rechazaría igual.
 */
export function usePermissions(): { permissions: Set<Permission>; isLoading: boolean } {
  const { user, token } = useAuthStore();

  const { data, isLoading } = useQuery({
    queryKey: ['permissions-matrix', user?.phoneNumber, user?.activeRole],
    queryFn: getPermissionsMatrix,
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const fallback = user?.permissionOverrides;
  const effective = Array.isArray(data?.effective)
    ? data.effective
    : (Array.isArray(fallback) ? fallback : []) as Permission[];

  return { permissions: new Set(effective), isLoading };
}

/** ¿El usuario actual tiene este permiso? */
export function usePermission(permission: Permission): boolean {
  const { permissions } = usePermissions();
  return permissions.has(permission);
}
